import * as React from 'react';
import { ReactElement, useEffect, useState } from 'react';
import { Button, createStyles, makeStyles, TextField } from '@material-ui/core';
import BasicModal from 'components/common/modal/BasicModal';
import { BackOrderOfferCreditSubscriptionType, ClosetUserCount } from 'types/api/backorder';
import styled from '@emotion/styled';
import { backOrderSubscriptionName, backOrderSubscriptionPrice } from 'constants/backorder';
import { getDateDifference } from 'modules/utils/time';
import NumberFormat, { NumberFormatValues } from 'react-number-format';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { DateTime } from 'luxon';

dayjs.extend(duration);

interface Props {
  subscriptionPlan: BackOrderOfferCreditSubscriptionType;
  subscriptionTerm: [string, string];
  closetUserCount: ClosetUserCount | null;
  onChangeOfferCredit: (credit: number) => void;
  onClose: () => void;
}

const CreditCalculationModal = ({
  closetUserCount,
  subscriptionPlan,
  subscriptionTerm,
  onChangeOfferCredit,
  onClose,
}: Props): ReactElement => {
  const [licenseCount, setLicenseCount] = useState<number>(0);
  const [unitPrice, setUnitPrice] = useState<number>(0);
  const [lineTotal, setLineTotal] = useState<number>(0);
  const [period, setPeriod] = useState<{ months: number; days: number } | null>(null);
  const subscriptionPlanPrice = backOrderSubscriptionPrice[subscriptionPlan];
  const totalSubscriptionDay =
    getDateDifference(subscriptionTerm[0], subscriptionTerm[1], 'day') + 1;
  const modalClasses = makeStyles(() =>
    createStyles({
      paper: {
        'max-width': '780px',
      },
    }),
  )();

  const onConfirm = (): void => {
    onChangeOfferCredit(lineTotal);
    onClose();
  };

  useEffect(() => {
    if (!subscriptionPlan || !subscriptionTerm[0] || !subscriptionTerm[1]) return;
    /**
     * Initial unit price 계산
     * 월 이용료: 총 full 개월수 * price
     * 일 이용료: price / end Date 해당 달의 일 수 (소수점 3째 자리에서 반올림)
     * 총 이용료: 월 이용료 + 일 이용료
     */
    const [start, end] = subscriptionTerm;
    const startDate = DateTime.fromFormat(start, 'yyyy-MM-dd');
    const endDate = DateTime.fromFormat(end, 'yyyy-MM-dd').plus({
      day: 1,
    });

    const duration = endDate.diff(startDate, ['months', 'days']);
    const months = duration.get('months');
    const days = duration.get('days');
    setPeriod({ months, days });

    const leftDaysOfTheLastMonth = endDate.daysInMonth ?? 0;
    const dayPriceBasedOnTheEndMonth = subscriptionPlanPrice / leftDaysOfTheLastMonth;
    const priceForMonths = subscriptionPlanPrice * months;
    const priceForDays = dayPriceBasedOnTheEndMonth * days;
    const totalPrice = priceForMonths + priceForDays;

    setUnitPrice(parseFloat(totalPrice.toFixed(2)));
  }, [subscriptionPlan, subscriptionTerm]);

  useEffect(() => {
    //* 소수점 2번째 자리까지 출력
    const res = unitPrice * licenseCount;
    setLineTotal(parseFloat(res.toFixed(2)));
  }, [unitPrice, licenseCount]);

  return (
    <BasicModal
      title="Credit Calculation"
      open={true}
      onClose={onClose}
      classes={modalClasses}
      actionButton={
        <Button variant={'text'} color={'primary'} onClick={onConfirm} disabled={false}>
          Confirm
        </Button>
      }
    >
      <ContentContainer>
        <ContentWrapper>
          <ContentRow>
            <Label>{backOrderSubscriptionName[subscriptionPlan]}</Label>
            <Description>
              <BoldTextWrapper>{`${subscriptionPlanPrice}.00`}/</BoldTextWrapper>
              month, user
            </Description>
          </ContentRow>
          <ContentRow>
            <Label>Period (Subscription Term)</Label>
            <Description>
              {subscriptionTerm[0]} - {subscriptionTerm[1]} ({totalSubscriptionDay}days:{' '}
              {`${period?.months}months ${period?.days} days`})
            </Description>
          </ContentRow>
          <ContentRow>
            <Label>License Count (No. of Users)</Label>
            <Description gap={10}>
              <NumberFormat
                style={{ width: '120px' }}
                customInput={TextField}
                value={licenseCount}
                thousandSeparator
                decimalScale={0}
                allowNegative={false}
                onValueChange={(value: NumberFormatValues) => {
                  if (!value.floatValue) return;
                  setLicenseCount(value.floatValue);
                }}
                onBlur={({ target: { value } }: React.FocusEvent<HTMLInputElement>) => {
                  const floatNumber = Number(value.replace(/,/gi, ''));
                  setLicenseCount(Math.floor(floatNumber * 100) / 100);
                }}
              />
              Users
              <div>
                *Current Total
                <BoldTextWrapper>{`${closetUserCount?.totalUserCount ?? 0} `}</BoldTextWrapper>
                Users (Including
                {closetUserCount?.supportUserCount ?? 0} CLO Support)
              </div>
            </Description>
          </ContentRow>
        </ContentWrapper>
        <ContentWrapper>
          <ContentRow>
            <Label>Unit Price</Label>
            <Description>
              <NumberFormat
                style={{ width: '120px' }}
                customInput={TextField}
                value={unitPrice}
                thousandSeparator
                decimalScale={2}
                allowNegative={false}
                onValueChange={(value: NumberFormatValues) => {
                  if (!value.floatValue) return;
                  setUnitPrice(value.floatValue);
                }}
                onBlur={({ target: { value } }: React.FocusEvent<HTMLInputElement>) => {
                  const floatNumber = Number(value.replace(/,/gi, ''));
                  setUnitPrice(Math.floor(floatNumber * 100) / 100);
                }}
              />
            </Description>
          </ContentRow>
          <ContentRow>
            <Label>Line Total (Offer Credit)</Label>
            <Description>
              <NumberFormat
                style={{ width: '120px' }}
                customInput={TextField}
                value={lineTotal}
                thousandSeparator
                decimalScale={2}
                allowNegative={false}
                onValueChange={(value: NumberFormatValues) => {
                  if (!value.floatValue) return;
                  setLineTotal(value.floatValue);
                }}
                onBlur={({ target: { value } }: React.FocusEvent<HTMLInputElement>) => {
                  const floatNumber = Number(value.replace(/,/gi, ''));
                  setLineTotal(Math.floor(floatNumber * 100) / 100);
                }}
              />
              <BoldTextWrapper>Credits</BoldTextWrapper>
            </Description>
          </ContentRow>
        </ContentWrapper>
        <InformationTextList>
          <li>The plan expires at 23:59 on the End Date (UTC+0).</li>
          <li>
            The last month with the end date is calculated as per the number of days. (Divide the
            amount by 31 for months with 31 days, divide the amount by 30 for months with 30 days)
          </li>
        </InformationTextList>
      </ContentContainer>
    </BasicModal>
  );
};

export default CreditCalculationModal;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;
const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ContentRow = styled.div`
  display: flex;
  align-items: center;
`;

const Label = styled.div`
  flex-basis: 30%;
  font-weight: 600;
`;

const Description = styled.div<{ gap?: number }>`
  display: flex;
  align-items: center;
  ${({ gap }) => gap && `gap: ${gap}px`}
`;

const BoldTextWrapper = styled.span`
  font-weight: 600;
`;

const InformationTextList = styled.ul`
  margin: 0;
  list-style: disc;
  position: relative;
  left: 5px;
  text-indent: 0;
  padding-left: 0;

  & > li {
    font-size: 13px;
  }
`;
